import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { API_BASE } from '../Constants';
import { safeStorage } from '../../utils/safeStorage';

const DISPLAY_NAME_STORAGE_KEY = 'maritalk_display_name';

export interface UserSettings {
  display_name: string;
  default_instructions: string;
  tts_voice: string;
}

export interface UserSettingsContextValue {
  displayName: string;
  defaultInstructions: string;
  ttsVoice: string;
  loading: boolean;
  saving: boolean;
  refresh: () => Promise<void>;
  save: (patch: Partial<UserSettings>) => Promise<void>;
}

const UserSettingsContext = createContext<UserSettingsContextValue | null>(null);

export const useUserSettings = (): UserSettingsContextValue => {
  const ctx = useContext(UserSettingsContext);
  if (!ctx) {
    throw new Error('useUserSettings must be used within a UserSettingsProvider');
  }
  return ctx;
};

interface ProviderProps {
  children: React.ReactNode;
}

export const UserSettingsProvider: React.FC<ProviderProps> = ({ children }) => {
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();

  // Hydrate the name from localStorage for an instant first paint (avoids the
  // email flashing before the network round-trip completes).
  const [displayName, setDisplayName] = useState<string>(
    () => safeStorage.getItem(DISPLAY_NAME_STORAGE_KEY) || ''
  );
  const [defaultInstructions, setDefaultInstructions] = useState<string>('');
  const [ttsVoice, setTtsVoice] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const applySettings = useCallback((settings: Partial<UserSettings> | null | undefined) => {
    const name = (settings?.display_name || '').trim();
    setDisplayName(name);
    setDefaultInstructions(settings?.default_instructions || '');
    setTtsVoice(settings?.tts_voice || '');
    if (name) {
      safeStorage.setItem(DISPLAY_NAME_STORAGE_KEY, name);
    } else {
      safeStorage.removeItem(DISPLAY_NAME_STORAGE_KEY);
    }
  }, []);

  const refresh = useCallback(async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const resp = await fetch(`${API_BASE}/api/user/settings`, {
        headers: { Authorization: `Auth ${token}` },
      });
      if (resp.ok) {
        const data = await resp.json();
        applySettings(data?.settings);
      }
    } catch {
      /* keep hydrated/local values on failure */
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, getAccessTokenSilently, applySettings]);

  const save = useCallback(async (patch: Partial<UserSettings>) => {
    setSaving(true);
    try {
      const token = await getAccessTokenSilently();
      const resp = await fetch(`${API_BASE}/api/user/settings`, {
        method: 'POST',
        headers: { Authorization: `Auth ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      if (resp.ok) {
        const data = await resp.json();
        applySettings(data?.settings);
      }
    } finally {
      setSaving(false);
    }
  }, [getAccessTokenSilently, applySettings]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <UserSettingsContext.Provider
      value={{ displayName, defaultInstructions, ttsVoice, loading, saving, refresh, save }}
    >
      {children}
    </UserSettingsContext.Provider>
  );
};
